/**
 * Экран приветствия: квокка зовёт начать.
 *
 * Первый экран задаёт тон всему приложению, поэтому здесь есть только
 * одно действие — иначе новичок выбирает между кнопками, ещё не поняв,
 * что происходит. Собственного состояния у экрана нет: единственный
 * переход уводит в онбординг.
 *
 * Крупный маскот + короткая фраза читаются за секунду; подзаголовок
 * намеренно обещает «не так, как все», но не раскрывает скрытую
 * диагностику — это интрига, а не описание механики.
 *
 * Уже вошедший ученик стартовый экран не видит: при открытии домена его
 * сразу уводит в раздел обучения. Приветствие — только для гостя.
 */

import { motion } from 'framer-motion';
import { Navigate, useNavigate } from 'react-router-dom';

import { Button } from '../components/Button';
import { Quokka } from '../components/Quokka';
import { isSignedIn, useUserStore } from '../store/userStore';
import { useTranslation } from 'react-i18next';

export function WelcomeScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const onboarded = useUserStore((state) => state.onboardedAt !== null);
  const signedIn = useUserStore(isSignedIn);

  // Вошёл — сразу в учёбу: стартовый экран для гостя.
  if (signedIn) return <Navigate to="/map" replace />;
  // Знакомство уже было — стартовый экран сразу ведёт на вход.
  if (onboarded) return <Navigate to="/login" replace state={{ from: 'onboarding' }} />;

  return (
    <section className="shell flex flex-col items-center gap-8 text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 140, damping: 18 }}
      >
        <Quokka mood="welcome" alt={t('welcome.alt')} size="2xl" />
      </motion.div>

      <motion.div
        className="measure flex flex-col gap-3"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.35, ease: 'easeOut' }}
      >
        <h1 className="t-display">
          {t('welcome.title')}
        </h1>
        <p className="text-body text-muted">
          {t('welcome.text')}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.35, ease: 'easeOut' }}
        className="measure"
      >
        <Button fullWidth onClick={() => navigate('/onboarding')}>
          {t('welcome.go')}
        </Button>
        <button type="button" onClick={() => navigate('/login', { state: { from: 'welcome' } })} className="mt-2 min-h-11 w-full text-center text-sm text-muted transition-colors hover:text-heading">
          {t('auth.haveAccount')} <span className="font-bold text-accent-dark">{t('auth.signIn')}</span>
        </button>
      </motion.div>
    </section>
  );
}
